import { useMemo } from "react";
import type { KeywordSearchConfig } from "@/features/chat/utils/keywordSearchConfigs";

type SearchMode = "keyword" | "participants";

interface UseChatSearchViewStateOptions {
  searchMode: SearchMode;
  searchPerformed: boolean;
  isCheckingExisting: boolean;
  participantsError: string | null;
  existingDmChats: ReadonlyArray<unknown>;
  existingTopicChats: ReadonlyArray<unknown>;
  keywordSearchConfigs: KeywordSearchConfig[];
}

export const useChatSearchViewState = ({
  searchMode,
  searchPerformed,
  isCheckingExisting,
  participantsError,
  existingDmChats,
  existingTopicChats,
  keywordSearchConfigs,
}: UseChatSearchViewStateOptions) => {
  const keywordIsLoading = useMemo(
    () => keywordSearchConfigs.some((config) => config.isLoading),
    [keywordSearchConfigs],
  );
  const keywordError = useMemo(
    () => keywordSearchConfigs.find((config) => config.error)?.error ?? null,
    [keywordSearchConfigs],
  );
  const keywordResults = useMemo(
    () => keywordSearchConfigs.map((config) => config.response?.results || []),
    [keywordSearchConfigs],
  );

  const isPreparingData =
    searchMode === "participants" ? isCheckingExisting : false;
  const isLoading =
    searchMode === "keyword" ? keywordIsLoading : isCheckingExisting;
  const hasError = searchMode === "keyword" ? keywordError : participantsError;
  const errorMessage = hasError;

  const noResults =
    searchPerformed &&
    !isLoading &&
    !hasError &&
    (searchMode === "participants"
      ? existingDmChats.length === 0 && existingTopicChats.length === 0
      : keywordResults.every((results) => results.length === 0));

  return {
    keywordResults,
    isPreparingData,
    isLoading,
    hasError,
    errorMessage,
    noResults,
  };
};
